// trackerCookies — delete the first-party cookies the ad tags wrote once a
// visitor withdraws consent.
//
// Revoking a tag (Consent Mode denied, fbq('consent','revoke'), oaiq('consent',
// false)) stops it SENDING, but whatever it already wrote stays in the browser:
// `_gcl_au` lives 90 days, `_fbp` 90, `__obref` a year. Withdrawal that leaves
// the identifiers behind is only half a withdrawal. OpenAI's SDK clears its own
// pair on consent=false, but only when it was loaded on this page, so we clear
// all of them ourselves regardless of which tags ran.
//
// The match is deliberately narrow — exact names plus one `startsWith` prefix —
// because the same jar holds Stemne's own `csrf_token`, `refresh_token` and
// `stemne_workspace`, and deleting one of those on a banner click would log the
// visitor out.

/** Exact names: Meta (`_fbp`, `_fbc`) and OpenAI (`__oppref`, `__obref`). */
export const TRACKER_COOKIE_NAMES: readonly string[] = ['_fbp', '_fbc', '__oppref', '__obref']

/** Google Ads conversion linker writes a family: `_gcl_au`, `_gcl_aw`, `_gcl_dc`,
 *  `_gcl_gb`, `_gcl_gs`, `_gcl_ag`, and more as Google adds them. */
export const TRACKER_COOKIE_PREFIXES: readonly string[] = ['_gcl_']

const IPV4 = /^\d{1,3}(\.\d{1,3}){3}$/

/** Every Domain attribute a tracker cookie on `hostname` could carry.
 *
 *  A delete only lands when its Domain matches the one the cookie was set with.
 *  gtag (`cookie_domain: 'auto'`) and fbevents write on the highest settable
 *  domain (`.stemne.no`), but nothing stops a host-level one, so we try every
 *  suffix with at least two labels. Setting a cookie on a public suffix (`no`)
 *  is refused by the browser anyway, so stopping at two labels loses nothing.
 *  An IP address has no parent domain: splitting `127.0.0.1` would yield
 *  `0.0.1`, which is not a domain at all. */
export function cookieDomainCandidates(hostname: string): string[] {
  if (!hostname || IPV4.test(hostname)) return []
  const labels = hostname.split('.')
  const candidates: string[] = []
  for (let i = 0; i <= labels.length - 2; i++) {
    candidates.push(labels.slice(i).join('.'))
  }
  return candidates
}

function isTrackerCookie(name: string): boolean {
  return (
    TRACKER_COOKIE_NAMES.includes(name) ||
    TRACKER_COOKIE_PREFIXES.some((prefix) => name.startsWith(prefix))
  )
}

/** Expire every tracker cookie visible to this page. Leaves every other cookie
 *  alone. Best effort: a sandboxed frame can throw on cookie access, and a
 *  failed cleanup must never break the decline that triggered it. */
export function clearTrackerCookies(hostname?: string): void {
  if (typeof document === 'undefined') return
  try {
    const host = hostname ?? window.location.hostname
    const names = new Set(
      document.cookie
        .split(';')
        .map((pair) => pair.split('=')[0]?.trim() ?? '')
        .filter(isTrackerCookie),
    )
    const domains = cookieDomainCandidates(host)
    for (const name of names) {
      // Host-only first, then each Domain variant. Per-write try so one
      // refused delete doesn't leave the rest in place.
      for (const suffix of ['', ...domains.map((d) => `; domain=${d}`)]) {
        try {
          // biome-ignore lint/suspicious/noDocumentCookie: Cookie Store API is missing in Firefox and older Safari, and the delete must be synchronous
          document.cookie = `${name}=; Max-Age=0; path=/${suffix}`
        } catch {
          // Refused write — nothing more we can do for this variant.
        }
      }
    }
  } catch {
    // Cookie access itself is blocked; there is nothing we could delete.
  }
}
